import type { ReactNode } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { Box, Stack } from "@similarweb/ui-components/foundations";

import { useConversionRateAvailability } from "app/pages/website-analysis/TrafficAndEngagement/ConversionsOverTime/useConversionRateAvailability";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState } from "single-spa/store/types";

import { AiInsightsWidget } from "./AiInsights/AiInsightsWidget";
import { getCanShowAiInsightsCard } from "./AiInsights/getAiInsightsAccess";
import { ConversionRateWidget } from "./ConversionRate/ConversionRateWidget";
import { DeviceDistributionWidget } from "./DeviceDistribution/DeviceDistributionWidget";
import { MonthlyActiveUsersWidget } from "./MonthlyActiveUsers/MonthlyActiveUsersWidget";
import { TotalVisitsWidget } from "./TotalVisits/TotalVisitsWidget";
import { WebsiteRankWidget } from "./WebsiteRank/WebsiteRankWidget";

const WidgetsGrid = styled.div<{ $extraKpiCount: number }>`
    display: grid;
    gap: 24px;
    margin-bottom: 24px;
    width: 100%;

    grid-template-columns: ${({ $extraKpiCount }) => ($extraKpiCount > 0 ? "repeat(2, 1fr)" : "repeat(3, 1fr)")};
    grid-template-rows: ${({ $extraKpiCount }) => {
        if ($extraKpiCount === 0) {
            return "250px";
        }
        if ($extraKpiCount === 1) {
            return "214px 250px";
        }
        return "214px 250px 250px";
    }};

    & > * {
        min-width: 0;
    }
`;

const WidgetsRow = styled(Box)<{ $columns: number; $minHeight: string }>`
    grid-template-columns: ${({ $columns }) => `repeat(${$columns}, 1fr)`};
    grid-auto-rows: minmax(${({ $minHeight }) => $minHeight}, auto);

    & > * {
        min-width: 0;
    }
`;

const INSIGHTS_ROW_MIN_HEIGHT = "264px";
const METRICS_ROW_MIN_HEIGHT = "250px";

interface ITotalVisitsComponentsContainerProps {
    /**
     * Cards rendered between Total visits and AI insights in a dedicated first row. When provided, the
     * remaining widgets move to a second row; each row has one column per card it shows.
     */
    insightCards?: ReactNode;
}

const TotalVisitsComponentsContainer = ({ insightCards }: ITotalVisitsComponentsContainerProps) => {
    const showGAApprovedData = useSelector((state: RootState) => state.common.showGAApprovedData);
    const swConnectedAccountsService = connectedAccountsServiceFactory();
    const params = proNavigator.getParams();
    const { apiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(params.country);
    const duration = isWWWithoutPermissions ? "3m" : params.duration;
    const { comparedDuration, webSource, country, key } = params;

    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const dateRangeText = `${durationObject.raw.from.format(shortMonthAndYear)} - ${durationObject.raw.to.format(shortMonthAndYear)}`;
    const countryText = CountryService.getCountryById(country)?.text ?? "";

    const { compareFrom, compareTo } = durationObject.forAPI;
    const apiParams = {
        ...proNavigator.getApiParams({ ...params, duration }),
        compareFrom,
        compareTo,
        duration,
    };

    const isGAPrivate = swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(apiParams.keys ?? key);
    const conversionRateAvailability = useConversionRateAvailability({
        keys: [apiParams.key],
        from: apiParams.from,
        to: apiParams.to,
        isWWW: apiParams.isWWW,
    });
    // The availability hook no longer reports "hidden" while it loads, so keep the grid on three
    // columns until it settles rather than reserving space for a widget that may never render.
    const hasConversionRateWidget =
        !conversionRateAvailability.isLoading && conversionRateAvailability.state !== "hidden";
    const hasAiInsightsWidget = getCanShowAiInsightsCard(apiParams.key ?? key);
    const extraKpiCount = Number(hasConversionRateWidget) + Number(hasAiInsightsWidget);

    const totalVisitsWidget = (
        <TotalVisitsWidget
            params={apiParams}
            dateRangeText={dateRangeText}
            countryText={countryText}
            countryCode={country}
            showGAApprovedData={showGAApprovedData}
            isGAPrivate={isGAPrivate}
            apiController={apiController}
        />
    );

    const aiInsightsWidget = hasAiInsightsWidget ? (
        <AiInsightsWidget
            params={{ ...apiParams, key: apiParams.key ?? key }}
            dateRangeText={dateRangeText}
            countryText={countryText}
            countryCode={Number(country)}
        />
    ) : null;

    const conversionRateWidget = (
        <ConversionRateWidget
            params={apiParams}
            dateRangeText={dateRangeText}
            countryText={countryText}
            countryCode={country}
            availability={conversionRateAvailability}
        />
    );

    const audienceWidget =
        webSource === devicesTypes.TOTAL ? (
            <DeviceDistributionWidget
                params={apiParams}
                dateRangeText={dateRangeText}
                countryText={countryText}
                countryCode={country}
                showGAApprovedData={showGAApprovedData}
                isGAPrivate={isGAPrivate}
                apiController={apiController}
            />
        ) : (
            <MonthlyActiveUsersWidget
                params={apiParams}
                showGAApprovedData={showGAApprovedData}
                isGAPrivate={isGAPrivate}
            />
        );

    const websiteRankWidget = (
        <WebsiteRankWidget
            params={apiParams}
            apiController={apiController}
        />
    );

    if (insightCards) {
        return (
            <Stack
                gap="24px"
                width="100%"
                margin="0 0 24px"
            >
                <WidgetsRow
                    display="grid"
                    gap="24px"
                    $columns={hasAiInsightsWidget ? 3 : 2}
                    $minHeight={INSIGHTS_ROW_MIN_HEIGHT}
                >
                    {totalVisitsWidget}
                    {insightCards}
                    {aiInsightsWidget}
                </WidgetsRow>
                <WidgetsRow
                    display="grid"
                    gap="24px"
                    $columns={hasConversionRateWidget ? 3 : 2}
                    $minHeight={METRICS_ROW_MIN_HEIGHT}
                >
                    {hasConversionRateWidget ? conversionRateWidget : null}
                    {audienceWidget}
                    {websiteRankWidget}
                </WidgetsRow>
            </Stack>
        );
    }

    return (
        <WidgetsGrid $extraKpiCount={extraKpiCount}>
            {totalVisitsWidget}
            {aiInsightsWidget}
            {conversionRateWidget}
            {audienceWidget}
            {websiteRankWidget}
        </WidgetsGrid>
    );
};

export default TotalVisitsComponentsContainer;
